import type { FetchMiddleware } from '@backstage/core-app-api';
import type { DiscoveryApi, OAuthApi } from '@backstage/frontend-plugin-api';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// POSTs that read and change nothing; the proxy treats them as reads too.
const READ_ONLY_POST_PATHS = new Set(['/markdown']);

function isWrite(request: Request, baseUrl: string): boolean {
  if (!WRITE_METHODS.has(request.method)) {
    return false;
  }
  const path = request.url.slice(baseUrl.length).split('?')[0];
  return !(request.method === 'POST' && READ_ONLY_POST_PATHS.has(path));
}

/** The scopes the GitHub sign-in holds, as Backstage asks for them on refresh. */
const GITHUB_SESSION_SCOPE = 'read:user repo';

/** GitHub's answer to a token it no longer accepts. */
const isBadCredentials = async (response: Response) => {
  if (response.status !== 401) return false;
  try {
    const body = (await response.clone().json()) as {
      message?: string;
      error?: { message?: string };
    };
    return (body.message ?? body.error?.message) === 'Bad credentials';
  } catch {
    return false;
  }
};

/**
 * A fresh GitHub token from the auth backend, the same refresh Backstage's
 * GitHub session makes; undefined when it can't get one.
 */
export const refreshGitHubToken =
  (discoveryApi: DiscoveryApi, environment: string) =>
  async (): Promise<string | undefined> => {
    const baseUrl = await discoveryApi.getBaseUrl('auth');
    const query = new URLSearchParams({
      optional: 'true',
      scope: GITHUB_SESSION_SCOPE,
      env: environment,
    });
    const response = await globalThis.fetch(
      `${baseUrl}/github/refresh?${query}`,
      {
        headers: { 'x-requested-with': 'XMLHttpRequest' },
        credentials: 'include',
      },
    );
    if (!response.ok) return undefined;
    const body = (await response.json()) as {
      providerInfo?: { accessToken?: string };
    };
    return body.providerInfo?.accessToken || undefined;
  };

/**
 * Adds the user's connected GitHub token to requests through the
 * `application-portal` proxy.
 *
 * - Reads (including Markdown rendering): the proxy reads GitHub as the user, so the token is attached when
 *   the user has connected GitHub. It never opens a sign-in prompt; without a
 *   token the proxy serves only shared repositories and otherwise answers
 *   `GitHubNotConnectedError`, and pages show their Connect prompt.
 * - Writes: the proxy writes with the GitHub App and uses the token to learn
 *   the user's login and check their own access, so the user is asked to
 *   connect GitHub the first time.
 * - A token GitHub rejects ("Bad credentials") is replaced once with a fresh
 *   one and the request retried. The GitHub App's user tokens expire and
 *   rotate, so another tab renewing the session leaves this tab's copy
 *   revoked; the fresh token is used from then on instead of that copy.
 *
 * Requests that carry their own token (`/user-actions`) keep it unless it is
 * one already replaced.
 */
export function githubTokenMiddleware(options: {
  discoveryApi: DiscoveryApi;
  githubAuthApi: OAuthApi;
  /** Defaults to the auth backend's GitHub refresh. */
  refreshToken?: () => Promise<string | undefined>;
  /** The auth environment (`auth.environment`), for the default refresh. */
  authEnvironment?: string;
}): FetchMiddleware {
  const refreshToken =
    options.refreshToken ??
    refreshGitHubToken(
      options.discoveryApi,
      options.authEnvironment ?? 'development',
    );
  // Rejected token → the fresh one that replaced it.
  const replacements = new Map<string, string>();
  let refreshing: Promise<string | undefined> | undefined;

  const replace = (rejected: string, fresh: string) => {
    for (const [token, replacement] of replacements) {
      if (replacement === rejected) replacements.set(token, fresh);
    }
    replacements.set(rejected, fresh);
  };

  // One refresh for every request that failed at the same time: each refresh
  // rotates the token, so parallel ones would revoke each other.
  const freshTokenFor = async (rejected: string) => {
    const known = replacements.get(rejected);
    if (known) return known;
    refreshing ??= refreshToken()
      .catch(() => undefined)
      .finally(() => {
        refreshing = undefined;
      });
    const fresh = await refreshing;
    if (fresh && fresh !== rejected) replace(rejected, fresh);
    return fresh && fresh !== rejected ? fresh : undefined;
  };

  const sessionToken = async (request: Request, baseUrl: string) =>
    isWrite(request, baseUrl)
      ? options.githubAuthApi.getAccessToken(['repo'])
      : options.githubAuthApi
          .getAccessToken(['repo'], { optional: true })
          .catch(() => '');

  return {
    apply: next => async (input, init) => {
      const request = new Request(input, init);
      const baseUrl = await options.discoveryApi.getBaseUrl(
        'application-portal',
      );
      if (!request.url.startsWith(`${baseUrl}/`)) {
        return next(request);
      }

      const ownToken =
        request.headers.get('x-github-token') ??
        (await sessionToken(request, baseUrl));
      const token = ownToken && (replacements.get(ownToken) ?? ownToken);
      // Kept unsent, so a write's body can go again.
      const retry = token ? request.clone() : undefined;
      if (token) {
        request.headers.set('x-github-token', token);
      }
      const response = await next(request);
      if (!token || !retry || !(await isBadCredentials(response))) {
        return response;
      }

      const fresh = await freshTokenFor(token);
      if (!fresh) return response;
      retry.headers.set('x-github-token', fresh);
      return next(retry);
    },
  };
}
